import React, {memo, useEffect, useRef} from 'react';
import {createPortal} from 'react-dom';
import {styled, keyframes} from 'styled-components';
import {forbidExtraProps} from 'airbnb-prop-types';
import {bool} from 'prop-types';
import {createPopper} from '@popperjs/core';
import {headerTextHover, bgAccentMain, focusNormal} from 'r/Design';
import {isReactElement, reactElement} from 'r/utils';
import {zHoverTooltip} from 'r/constants/zIdxs';

const borderAnimation = keyframes`
  0% {
    outline-offset: 2px;
    outline-color: #0172CB00;
  }
  10% {
    outline-offset: 2px;
    outline-color: #0172CB33;
  }
  25% {
    outline-offset: 4px;
    outline-color: #0172CBBB;
  }
  50% {
    outline-offset: 6px;
    outline-color: #0172CB33;
  }
  75% {
    outline-offset: 4px;
    outline-color: #0172CBBB;
  }
  90% {
    outline-offset: 2px;
    outline-color: #0172CB33;
  }
  100% {
    outline-offset: 2px;
    outline-color: #0172CB00;
  }
`;

const Tooltip = styled.div`
  background: ${bgAccentMain};
  border-radius: 8px;
  color: ${headerTextHover};
  z-index: ${zHoverTooltip};
  padding: 16px 16px 20px;
  text-align: center;
  box-shadow: 0px 4px 10px 0px rgba(170, 178, 197, 0.20);
  `;

const ElementOutline = styled.div`
  outline: 1px solid;
  border-radius: 500px;
  animation: ${borderAnimation} 2.3s ease-in-out infinite;
  button {
    border: none;
    &:hover {
      border: none;
    }
  }
`;

const ARROW_WIDTH = 28;

const Arrow = styled.div`
  position: absolute;
  width: ${ARROW_WIDTH}px;
  height: ${ARROW_WIDTH}px;
  border-radius: 4px;
  background: none;
  top: -12px;
  left: calc(50% - ${ARROW_WIDTH / 2}px);
`;

export default memo(WithHint);

WithHint.propTypes = forbidExtraProps({
  isHintShown: bool,
  element: reactElement.isRequired,
  hint: reactElement.isRequired,
});

function WithHint({
  isHintShown = false,
  element,
  hint,
}) {

  useEffect(() => {
    if (isReactElement(element)) {
      throw new Error('Feature doesn\'t support React components. Wrap it in native tag like `div` or styled component like `styled.div`');
    }
  }, [element]);

  const popperRef = useRef();
  const wrRef = useRef();
  const tooltipRef = useRef();

  useEffect(() => {
    if (isHintShown) {
      popperRef.current = createPopper(
        wrRef.current,
        tooltipRef.current,
        {
          placement: 'bottom',
          modifiers: [
            {
              name: 'offset',
              options: {offset: [0, 19]},
            },
            {
              name: 'arrow',
              options: {
                padding: 5,
              },
            },
          ],
        },
      );
    }
    return () => {
      if (isHintShown) {
        popperRef.current.destroy();
      }
    };
  }, [isHintShown]);

  const elementProps = {
    ref: wrRef,
  };

  if (isHintShown) {
    elementProps.style = {zIndex: zHoverTooltip, border: `1px solid ${focusNormal}`, borderRadius: '500px'};
  }

  return (
    <>
      {isHintShown && createPortal(
        <Tooltip
          ref={tooltipRef}
          id="tooltip"
        >
          {hint}
          <Arrow id="arrow">
            <svg fill="none" height="20" viewBox="0 0 28 20" width="28" xmlns="http://www.w3.org/2000/svg">
              <path d="M12.3941 1.1634C13.1937 0.086176 14.8063 0.0861751 15.6059 1.1634L27.219 16.8079C28.1986 18.1276 27.2566 20 25.613 20H2.38695C0.743412 20 -0.198569 18.1276 0.781047 16.8079L12.3941 1.1634Z" fill={bgAccentMain}/>
            </svg>
          </Arrow>
        </Tooltip>,
        document.body,
      )}
      {React.cloneElement(isHintShown ? <ElementOutline>{element}</ElementOutline> : element, {...element.props, ...elementProps})}
    </>
  );
}
